import axios from 'axios';

/*
 * Analytics endpoints used by the Sites landing page.
 *
 * api.js does not export its axios instance, so this module builds one with
 * the same base URL and bearer-token pattern.  Keep the two in step if the
 * auth scheme changes.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({ baseURL: API_BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* GET /analytics/scheduled-pages?days=N
   -> { pages: [{ domain_id, hostname, slug, title, published_at, status }], days, generated_at }
   Pages are sorted ascending by published_at.  A failure returns an empty
   feed (and logs) so the landing page still renders its other cards. */
export const getScheduledPages = async (days = 7) => {
  try {
    const response = await api.get('/analytics/scheduled-pages', { params: { days } });
    const data = response.data || {};
    return {
      pages: Array.isArray(data.pages) ? data.pages : [],
      days: data.days ?? days,
      generated_at: data.generated_at || null,
    };
  } catch (error) {
    console.error('Get scheduled pages error:', error.response?.data || error.message);
    return { pages: [], days, generated_at: null, error: true };
  }
};

/* The three refresh endpoints are POSTs with no body.  They throw on failure
   so the caller can toast the error; on success the caller refetches the
   matching GET to pick up the new map and updated_at. */
const post = async (path) => {
  const response = await api.post(path);
  return response.data;
};

export const refreshPageHits = () => post('/analytics/refresh-page-hits');
export const refreshAdSenseEarnings = () => post('/analytics/refresh-adsense-earnings');
export const refreshAdImpressions = () => post('/analytics/refresh-ad-impressions');

/* GET /analytics/adsense-earnings-daily
   -> { daily: { host: { 'YYYY-MM-DD': earnings } }, start, end, updated_at }
   Empty until refresh-adsense-earnings has run once with the daily report. */
export const getAdSenseDaily = async () => {
  try {
    const response = await api.get('/analytics/adsense-earnings-daily');
    return response.data || { daily: {}, start: null, end: null, updated_at: null };
  } catch (error) {
    console.error('Get AdSense daily error:', error.response?.data || error.message);
    return { daily: {}, start: null, end: null, updated_at: null, error: true };
  }
};

/* GET /analytics/amazon-earnings
   -> { periods: [{ period_id, period_start, period_end, report_type, by_tag,
                    daily, totals, attribution, filename, uploaded_at, ... }],
        tags: { tag: [domain_id] }, fallback_tag, updated_at } */
export const getAmazonEarnings = async () => {
  try {
    const response = await api.get('/analytics/amazon-earnings');
    const data = response.data || {};
    return {
      periods: Array.isArray(data.periods) ? data.periods : [],
      tags: data.tags || {},
      fallback_tag: data.fallback_tag || 'developingtheweb',
      updated_at: data.updated_at || null,
    };
  } catch (error) {
    console.error('Get Amazon earnings error:', error.response?.data || error.message);
    return { periods: [], tags: {}, fallback_tag: 'developingtheweb', updated_at: null, error: true };
  }
};

/* POST /analytics/amazon-earnings/upload (multipart). Throws so the caller
   can show the parser's message (422) to the user. */
export const uploadAmazonReport = async (file, { periodStart, periodEnd, dryRun } = {}) => {
  const form = new FormData();
  form.append('file', file);
  if (periodStart) form.append('period_start', periodStart);
  if (periodEnd) form.append('period_end', periodEnd);
  form.append('dry_run', dryRun ? 'true' : 'false');
  const response = await api.post('/analytics/amazon-earnings/upload', form);
  return response.data;
};

export const deleteAmazonPeriod = async (periodId) => {
  const response = await api.delete(`/analytics/amazon-earnings/${encodeURIComponent(periodId)}`);
  return response.data;
};
